import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Upload, Download, Pencil, Trash2, FileText, X, Loader2, Play, FileSpreadsheet, Film, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILE_TYPE_CONFIG = {
  pdf: { icon: FileText, color: 'text-red-600 bg-red-50', label: 'PDF' },
  spreadsheet: { icon: FileSpreadsheet, color: 'text-green-600 bg-green-50', label: 'Spreadsheet' },
  video: { icon: Film, color: 'text-blue-600 bg-blue-50', label: 'Video' },
};

const ResourcesPage = () => {
  const { user, token } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [videoModal, setVideoModal] = useState(null);
  const [previewModal, setPreviewModal] = useState(null);
  const [formData, setFormData] = useState({
    name: '', description: '', date: new Date().toISOString().slice(0, 10),
    version: '1.0', active: true, file: null
  });

  const headers = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchResources = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/resources`, { headers: headers() });
      setResources(res.data);
    } catch { toast.error('Failed to load resources'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchResources(); }, [fetchResources]);

  const resetForm = () => {
    setFormData({ name: '', description: '', date: new Date().toISOString().slice(0, 10), version: '1.0', active: true, file: null });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (r) => {
    setFormData({ name: r.name, description: r.description, date: r.date, version: r.version, active: r.active, file: null });
    setEditingId(r.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error('Name is required'); return; }
    if (!editingId && !formData.file) { toast.error('Please select a file'); return; }

    setSubmitting(true);
    const fd = new FormData();
    if (formData.file) fd.append('file', formData.file);
    fd.append('name', formData.name);
    fd.append('description', formData.description);
    fd.append('date', formData.date);
    fd.append('version', formData.version);
    fd.append('active', formData.active);

    try {
      if (editingId) {
        await axios.put(`${API}/resources/${editingId}`, fd, { headers: { ...headers(), 'Content-Type': 'multipart/form-data' } });
        toast.success('Resource updated');
      } else {
        await axios.post(`${API}/resources`, fd, { headers: { ...headers(), 'Content-Type': 'multipart/form-data' } });
        toast.success('Resource uploaded');
      }
      resetForm();
      fetchResources();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save resource');
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this resource?')) return;
    try {
      await axios.delete(`${API}/resources/${id}`, { headers: headers() });
      toast.success('Resource deleted');
      fetchResources();
    } catch { toast.error('Failed to delete'); }
  };

  const handleDownload = async (r) => {
    try {
      const res = await axios.get(`${API}/resources/${r.id}/download`, { headers: headers(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = r.file_name || `${r.name}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const openVideo = (r) => {
    setVideoModal(r);
  };

  const openPreview = (r) => {
    setPreviewModal(r);
  };

  const getFileTypeInfo = (r) => {
    const ft = r.file_type || 'pdf';
    return FILE_TYPE_CONFIG[ft] || FILE_TYPE_CONFIG.pdf;
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-violet-600" /></div>;

  return (
    <div className="space-y-6" data-testid="resources-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="resources-heading">Resources</h1>
          <p className="text-slate-600 mt-1">{isAdmin ? 'Manage resources: PDFs, spreadsheets, and videos' : 'Access available resources, guides, and training videos'}</p>
        </div>
        {isAdmin && !showForm && (
          <Button onClick={() => setShowForm(true)} className="bg-violet-600 hover:bg-violet-700" data-testid="upload-resource-btn">
            <Upload className="w-4 h-4 mr-2" />Upload Resource
          </Button>
        )}
      </div>

      {/* Upload/Edit Form (Admin) */}
      {isAdmin && showForm && (
        <Card data-testid="resource-form">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle>{editingId ? 'Edit Resource' : 'Upload Resource'}</CardTitle>
            <Button variant="ghost" size="icon" onClick={resetForm}><X className="w-4 h-4" /></Button>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>File {editingId ? '(optional — leave empty to keep current)' : '*'}</Label>
                <Input
                  type="file"
                  accept=".pdf,.xlsx,.xls,.csv,.mp4"
                  onChange={(e) => setFormData({ ...formData, file: e.target.files[0] })}
                  data-testid="resource-file-input"
                />
                <p className="text-xs text-slate-500 mt-1">Accepted: PDF, XLSX, XLS, CSV, MP4</p>
              </div>
              <div>
                <Label>Resource Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Enter resource name" data-testid="resource-name-input" />
              </div>
              <div className="md:col-span-2">
                <Label>Description</Label>
                <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Enter description" data-testid="resource-description-input" />
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} data-testid="resource-date-input" />
              </div>
              <div>
                <Label>Version</Label>
                <Input value={formData.version} onChange={(e) => setFormData({ ...formData, version: e.target.value })} placeholder="1.0" data-testid="resource-version-input" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="active-flag" checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} className="w-4 h-4 rounded border-slate-300" data-testid="resource-active-checkbox" />
                <Label htmlFor="active-flag" className="cursor-pointer">Active</Label>
              </div>
              <div className="md:col-span-2 flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                <Button type="submit" className="bg-violet-600 hover:bg-violet-700" disabled={submitting} data-testid="resource-submit-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingId ? 'Update' : 'Upload'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Resources Grid */}
      {resources.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500"><FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" /><p>No resources available</p></CardContent></Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="resources-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left p-3 text-sm font-semibold text-slate-600">Type</th>
                  {isAdmin && <th className="text-left p-3 text-sm font-semibold text-slate-600">File Name</th>}
                  <th className="text-left p-3 text-sm font-semibold text-slate-600">Name</th>
                  <th className="text-left p-3 text-sm font-semibold text-slate-600">Description</th>
                  <th className="text-left p-3 text-sm font-semibold text-slate-600">Date</th>
                  <th className="text-left p-3 text-sm font-semibold text-slate-600">Version</th>
                  {isAdmin && <th className="text-left p-3 text-sm font-semibold text-slate-600">Active</th>}
                  <th className="text-left p-3 text-sm font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {resources.map((r) => {
                  const typeInfo = getFileTypeInfo(r);
                  const TypeIcon = typeInfo.icon;
                  const fileType = r.file_type || 'pdf';
                  return (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`resource-row-${r.id}`}>
                      <td className="p-3 text-sm">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${typeInfo.color}`}>
                          <TypeIcon className="w-3 h-3" />
                          {typeInfo.label}
                        </span>
                      </td>
                      {isAdmin && <td className="p-3 text-sm text-slate-700 max-w-[180px] truncate">{r.file_name}</td>}
                      <td className="p-3 text-sm font-medium text-slate-900">{r.name}</td>
                      <td className="p-3 text-sm text-slate-600 max-w-xs truncate">{r.description}</td>
                      <td className="p-3 text-sm text-slate-600">{r.date}</td>
                      <td className="p-3 text-sm text-slate-600">{r.version}</td>
                      {isAdmin && (
                        <td className="p-3 text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${r.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {r.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      )}
                      <td className="p-3 text-sm">
                        <div className="flex items-center gap-2">
                          {isAdmin ? (
                            <>
                              {fileType === 'video' && (
                                <Button variant="outline" size="sm" onClick={() => openVideo(r)} data-testid={`play-resource-${r.id}`}>
                                  <Play className="w-3 h-3 mr-1" />Play
                                </Button>
                              )}
                              {fileType === 'pdf' && (
                                <Button variant="outline" size="sm" onClick={() => openPreview(r)} data-testid={`preview-resource-${r.id}`}>
                                  <Eye className="w-3 h-3 mr-1" />Preview
                                </Button>
                              )}
                              {fileType !== 'video' && (
                                <Button variant="outline" size="sm" onClick={() => handleDownload(r)} data-testid={`download-resource-${r.id}`}>
                                  <Download className="w-3 h-3 mr-1" />Download
                                </Button>
                              )}
                              <Button variant="outline" size="sm" onClick={() => handleEdit(r)} data-testid={`edit-resource-${r.id}`}><Pencil className="w-3 h-3 mr-1" />Edit</Button>
                              <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(r.id)} data-testid={`delete-resource-${r.id}`}><Trash2 className="w-3 h-3 mr-1" />Delete</Button>
                            </>
                          ) : (
                            <>
                              {fileType === 'video' ? (
                                <Button variant="outline" size="sm" onClick={() => openVideo(r)} data-testid={`play-resource-${r.id}`}>
                                  <Play className="w-3 h-3 mr-1" />Play
                                </Button>
                              ) : fileType === 'pdf' ? (
                                <>
                                  <Button variant="outline" size="sm" onClick={() => openPreview(r)} data-testid={`preview-resource-${r.id}`}>
                                    <Eye className="w-3 h-3 mr-1" />View
                                  </Button>
                                  <Button variant="outline" size="sm" onClick={() => handleDownload(r)} data-testid={`download-resource-${r.id}`}>
                                    <Download className="w-3 h-3 mr-1" />Download
                                  </Button>
                                </>
                              ) : (
                                <Button variant="outline" size="sm" onClick={() => handleDownload(r)} data-testid={`download-resource-${r.id}`}>
                                  <Download className="w-3 h-3 mr-1" />Download
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Video Player Modal */}
      {videoModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="video-modal" onClick={(e) => { if (e.target === e.currentTarget) setVideoModal(null); }}>
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center gap-3">
                <Film className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-slate-900">{videoModal.name}</h2>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setVideoModal(null)} data-testid="video-modal-close">
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="bg-black">
              <video
                controls
                autoPlay
                className="w-full max-h-[70vh]"
                controlsList="nodownload"
                onContextMenu={(e) => e.preventDefault()}
                data-testid="video-player"
              >
                <source src={`${API}/resources/${videoModal.id}/stream?token=${token}`} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            {videoModal.description && (
              <div className="px-6 py-3 bg-slate-50 border-t border-slate-200">
                <p className="text-sm text-slate-600">{videoModal.description}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* PDF Preview Modal */}
      {previewModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="pdf-preview-modal" onClick={(e) => { if (e.target === e.currentTarget) setPreviewModal(null); }}>
          <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50 shrink-0">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-red-600" />
                <h2 className="text-lg font-semibold text-slate-900">{previewModal.name}</h2>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => handleDownload(previewModal)} data-testid="pdf-preview-download">
                  <Download className="w-4 h-4 mr-1" />Download
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setPreviewModal(null)} data-testid="pdf-preview-close">
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            <div className="flex-1 bg-slate-100">
              <iframe
                src={`${API}/resources/${previewModal.id}/stream?token=${token}`}
                className="w-full h-full border-0"
                title={previewModal.name}
                data-testid="pdf-preview-iframe"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourcesPage;
